import React, { useEffect, useRef, useState } from 'react';
import { Box, lighten, useTheme } from '@mui/system';
import { Avatar, Button, IconButton, Popover, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName, IconProp } from '@fortawesome/fontawesome-svg-core';
import { B2DSymboleIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { parseDateSafe, timeAgo } from '@europrocurement/l2d-utils';
import { faPaperPlane, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { CustomAvatar } from '../../molecules';
import { type MessageItem } from './MessageBox';

export type MessageLineProps = {
    message: MessageItem;
    previousAuthor: string;
    userIdentifier: string;
    user: {
        firstName?: string | null;
        lastName?: string | null;
        idUserGie?: number | null;
    } | null;
    anonymous: boolean;
    onClick: (fileName: string, id: number) => void;
    onEdit: (
        messageId: string,
        messageData: {
            type: string;
            message: string;
        },
    ) => void;
    onDelete: (messageId: string) => void;
};

export const MessageLine: React.FunctionComponent<MessageLineProps> = function (props) {
    const { message, previousAuthor, userIdentifier, user, anonymous, onClick, onEdit, onDelete } =
        props;
    const { palette } = useTheme();

    const isCurrentUser = message.data.userIdentifier === userIdentifier;

    const isSameAuthorAsPrevious = previousAuthor === message.data.userIdentifier;
    const date = parseDateSafe(message.data.date);
    const isValidDate = date != null && date instanceof Date && !Number.isNaN(date.getTime());
    const colorBackground = isCurrentUser ? 'info.main' : 'grey.200';
    const { type } = message.data.message;
    const background = type === 'emoji' || type === 'document' ? 'none' : colorBackground;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputEdit, setInputEdit] = useState('');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isHoveringPopover, setIsHoveringPopover] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const buttonRef = useRef<HTMLElement>(null);
    const open = Boolean(anchorEl);

    let userFullName = user
        ? `${user.firstName} ${user.lastName}`
        : `Chargement de l'utilisateur ... `;
    if (anonymous && user && user.idUserGie) {
        userFullName = '';
    }

    let userAvatar = user ? (
        <CustomAvatar keycloakId={message.data.userIdentifier} />
    ) : (
        <CustomAvatar
            firstName="Chargement"
            lastName="Utilisateur"
        />
    );

    if (anonymous && user && user.idUserGie) {
        userAvatar = <B2DSymboleIcon style={{ maxHeight: '24px' }} />;
    }

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setAnchorEl(buttonRef.current);
    };

    const handleMouseLeaveLine = () => {
        timeoutRef.current = setTimeout(() => {
            if (!isHoveringPopover) {
                setAnchorEl(null);
            }
        }, 200);
    };

    const handleMouseEnterPopover = () => {
        setIsHoveringPopover(true);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const handleMouseLeavePopover = () => {
        setIsHoveringPopover(false);
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 200);
    };

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    const handleModify = () => {
        setIsEditing(true);
        if ('message' in message.data.message) setInputEdit(message.data.message.message);
    };

    const handleModifyCancel = () => {
        setIsEditing(false);
        setInputEdit('');
    };

    const handleModifyAccept = () => {
        const messageData = {
            type: 'message',
            message: inputEdit,
        };
        onEdit(message.messageId, messageData);
        setIsEditing(false);
        setInputEdit('');
    };

    const handleDelete = () => {
        onDelete(message.messageId);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if ((e.metaKey || e.ctrlKey) && (e.code === 'Enter' || e.code === 'NumpadEnter')) {
            handleModifyAccept();
        }
    };

    return (
        <Box
            key={message.messageId}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: isSameAuthorAsPrevious ? 1 : 2,
                gap: isSameAuthorAsPrevious ? 1 : 2,
                position: 'relative',
            }}
        >
            {!isSameAuthorAsPrevious && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginLeft: 1,
                        justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                    }}
                >
                    {userAvatar}
                    <Typography
                        variant="l2dbodys"
                        color="text.secondary"
                        fontWeight={600}
                    >
                        {`${userFullName} `}
                        {isValidDate
                            ? date.toLocaleTimeString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                              })
                            : ''}
                    </Typography>
                    <Typography variant="l2dbodyxs">{isValidDate ? timeAgo(date) : ''}</Typography>
                </Box>
            )}
            <Box
                sx={{
                    display: isEditing ? 'none' : 'flex',
                    justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                    paddingX: 2,
                }}
            >
                <Box
                    ref={buttonRef}
                    id={`messageline-${message.messageId}`}
                    sx={{
                        maxWidth: '60%',
                        bgcolor: background,
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeaveLine}
                >
                    {(() => {
                        if (type === 'emoji') {
                            return (
                                <FontAwesomeIcon
                                    icon={message.data.message.icon as IconProp}
                                    size="2x"
                                    title={message.data.message.icon}
                                />
                            );
                        }

                        if (type === 'document') {
                            const fileName = message.data.message.dosDocFileName;
                            const fileId = message.data.message.dosDocFileId;

                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: '0 8px',
                                        alignItems: 'center',
                                        background: 'none',
                                        border: `solid 1px ${palette.grey[100]}`,
                                        borderRadius: '12px',
                                        padding: '8px',
                                        transition: '0.1s ease',
                                    }}
                                    data-testid="document-message"
                                >
                                    <Box>
                                        <Avatar
                                            data-testid="document-icon-message"
                                            sx={{
                                                display: 'flex',
                                                borderRadius: '8px',
                                                minHeight: '36px',
                                                minWidth: '36px',
                                                backgroundColor: lighten(palette.primary.main, 0.9),
                                            }}
                                        >
                                            <FaOptionIcon
                                                icon={['fasl', 'file']}
                                                sx={{
                                                    fontSize: '16px',
                                                    lineHeight: '16px',
                                                    fontWeight: '400',
                                                    color: palette.primary.main,
                                                }}
                                            />
                                        </Avatar>
                                    </Box>
                                    <Box sx={{}}>
                                        {fileName && (
                                            <Box
                                                sx={{
                                                    color: palette.primary.main,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        color: 'primary',
                                                    }}
                                                    variant="l2dbodys"
                                                    onClick={() => onClick(fileName, fileId)}
                                                >
                                                    {fileName}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }

                        // par défaut : type === 'message' (texte)
                        return (
                            <Typography
                                variant="l2dbodys"
                                dangerouslySetInnerHTML={{
                                    __html: message.data.message.message.replace(/\n/g, '<br />'),
                                }}
                            />
                        );
                    })()}
                </Box>
            </Box>
            {type === 'message' && isCurrentUser && (
                <Popover
                    id={`messageline-over-${message.messageId}`}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: isCurrentUser ? 'right' : 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: isCurrentUser ? 'right' : 'left',
                    }}
                    disableAutoFocus
                    disableEnforceFocus
                    disableRestoreFocus
                    sx={{ pointerEvents: 'none' }}
                    PaperProps={{
                        onMouseEnter: handleMouseEnterPopover,
                        onMouseLeave: handleMouseLeavePopover,
                        sx: {
                            pointerEvents: 'auto',
                            marginTop: '-20px',
                            display: 'flex',
                        },
                    }}
                >
                    <Button
                        color="inherit"
                        onClick={handleModify}
                        sx={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                            fontSize: '13px',
                            fontWeight: '500;',
                            lineHeight: '20.8px',
                            textAlign: 'left',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        <FaOptionIcon icon={['fad', 'fa-pen' as IconName]} />
                    </Button>
                    <Button
                        color="inherit"
                        onClick={handleDelete}
                        sx={{
                            color: 'error.main',
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'center',
                            fontSize: '13px',
                            fontWeight: '500;',
                            lineHeight: '20.8px',
                            textAlign: 'left',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        <FaOptionIcon icon={['fad', 'fa-ban' as IconName]} />
                    </Button>
                </Popover>
            )}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                }}
            >
                <Box
                    sx={{
                        display: isCurrentUser && isEditing ? 'flex' : 'none',
                        justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                        paddingX: 2,
                        width: '80%',
                        bgcolor: background,
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                        backgroundColor: 'text.gris005',
                    }}
                >
                    <TextField
                        variant="standard"
                        fullWidth
                        placeholder="Ecrire un message..."
                        value={inputEdit}
                        onChange={(e) => setInputEdit(e.target.value)}
                        onKeyDown={handleKeyPress}
                        InputProps={{ disableUnderline: true }}
                        multiline
                    />
                    <IconButton
                        color="primary"
                        onClick={handleModifyCancel}
                        sx={{
                            padding: '0',
                        }}
                    >
                        <FaOptionIcon
                            icon={faXmark}
                            size="2xs"
                            color="text.gris50"
                        />
                    </IconButton>
                    <IconButton
                        color="primary"
                        onClick={handleModifyAccept}
                        sx={{
                            padding: '0',
                        }}
                    >
                        <FaOptionIcon
                            icon={faPaperPlane}
                            size="2xs"
                            color="text.gris50"
                        />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};
